import { api } from './api'

const DEFAULT_INTERVAL_MS = 1000
const DEFAULT_TIMEOUT_MS = 600000

export function unwrapApiData(res) {
  const body = res?.data !== undefined ? res.data : res
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

export function asyncOperationFrom(value) {
  const data = unwrapApiData(value)
  if (data?.asyncOperation?.operationKey) return data.asyncOperation
  if (data?.operationKey) return data
  return null
}

export function normalizeAsyncPath(value) {
  let raw = String(value || '').trim()
  if (!raw) return ''
  try {
    if (/^https?:\/\//i.test(raw)) {
      const url = new URL(raw)
      raw = `${url.pathname}${url.search || ''}`
    }
  } catch {
    // Keep the original path when URL parsing fails.
  }
  if (!raw.startsWith('/')) raw = `/${raw}`
  return raw.replace(/^\/v\d+(?=\/)/i, '')
}

function sleep(ms) {
  const timer = (typeof window !== 'undefined' && window.setTimeout) ? window.setTimeout.bind(window) : setTimeout
  return new Promise((resolve) => timer(resolve, ms))
}

function asyncError(operation) {
  return new Error(operation?.errorMessage || '작업 처리에 실패했습니다.')
}

export async function waitForAsyncOperation(operation, options = {}) {
  let current = asyncOperationFrom(operation) || operation
  if (!current?.operationKey) return unwrapApiData(operation)

  const intervalMs = Math.max(250, Number(options.intervalMs || DEFAULT_INTERVAL_MS) || DEFAULT_INTERVAL_MS)
  const timeoutMs = Math.max(intervalMs, Number(options.timeoutMs || DEFAULT_TIMEOUT_MS) || DEFAULT_TIMEOUT_MS)
  const startedAt = Date.now()

  while (true) {
    const status = String(current?.status || '').toUpperCase()
    if (status === 'SUCCEEDED') return current
    if (status === 'FAILED') throw asyncError(current)
    if (Date.now() - startedAt > timeoutMs) {
      throw new Error('작업 처리 시간이 초과되었습니다. 잠시 후 다시 확인해 주세요.')
    }

    await sleep(intervalMs)
    const pollPath = normalizeAsyncPath(current.pollUrl || `/async-operations/${current.operationKey}`)
    const res = await api.get(pollPath, { __silent: true })
    current = unwrapApiData(res)
  }
}

export async function unwrapAsyncResult(res, options = {}) {
  const operation = asyncOperationFrom(res)
  if (!operation) return unwrapApiData(res)
  const completed = await waitForAsyncOperation(operation, options)
  return completed?.result || {}
}

async function parseMaybeJsonBlob(blob) {
  if (!blob || typeof blob.text !== 'function') return null
  const text = await blob.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

export async function resolveAsyncBlobResponse(res, options = {}) {
  const contentType = String(res?.headers?.['content-type'] || res?.headers?.get?.('content-type') || '').toLowerCase()
  const isJsonBlob = contentType.includes('application/json') && res?.data && typeof res.data.text === 'function'
  const payload = isJsonBlob ? await parseMaybeJsonBlob(res.data) : unwrapApiData(res)
  const operation = asyncOperationFrom(payload)
  if (!operation) return res

  const completed = await waitForAsyncOperation(operation, options)
  const downloadPath = normalizeAsyncPath(completed?.downloadUrl || operation.downloadUrl)
  if (!downloadPath) throw new Error('다운로드 파일을 찾을 수 없습니다.')
  return api.get(downloadPath, { responseType: 'blob', __silent: true })
}
